import { useMemo, useState } from "react";
import { CalendarClock, Mail, MessageCircle, AlertTriangle } from "lucide-react";
import Modal from "@/components/Modal";
import { api, stageMeta } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { buildFollowupDraft } from "@/lib/outreach";
import { toast } from "sonner";

// Quick "when to send" presets, in days from now.
const PRESETS = [
  { label: "+3 days", days: 3 },
  { label: "+5 days", days: 5 },
  { label: "+1 week", days: 7 },
  { label: "+2 weeks", days: 14 },
];

// Now + `days`, formatted for a <input type="datetime-local"> (local time).
function plusDaysLocal(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

/**
 * FollowupComposer — the "Follow up" CTA target. Opens a stage-aware follow-up
 * draft for a deal, lets the rep pick channel + when-to-send + edit the copy,
 * and schedules it into the Follow-ups queue. Queued follow-ups don't send
 * themselves — when one is due, the rep hits "Send Now" on the Follow-ups
 * page to open a prefilled Gmail compose tab or WhatsApp chat.
 *
 * deal:     the deal (carries id, customer_id, stage, title, fob_value, cif_value, exworks_value, customer_name)
 * customer: the customer (carries emails[], phones[], contact) — may be a stub
 */
export default function FollowupComposer({ deal, customer, onClose, onScheduled }) {
  const { user } = useAuth();

  // Body is identical across channels, so build it once; only the recipient and
  // whether the subject shows depend on the channel.
  const initial = useMemo(
    () => buildFollowupDraft(deal, customer, user, "whatsapp"),
    [deal, customer, user]
  );

  const [channel, setChannel] = useState("whatsapp");
  const [subject, setSubject] = useState(initial.subject || "");
  const [message, setMessage] = useState(initial.body || "");
  const [when, setWhen] = useState(() => plusDaysLocal(3));
  const [saving, setSaving] = useState(false);

  const isEmail = channel === "email";
  const recipient = isEmail ? customer?.emails?.[0] || "" : customer?.phones?.[0] || "";
  const m = stageMeta(deal?.stage);

  const schedule = async () => {
    if (!recipient) {
      toast.error(isEmail ? "This customer has no email on file" : "This customer has no phone on file");
      return;
    }
    if (!message.trim()) {
      toast.error("Message is empty");
      return;
    }
    if (!when) {
      toast.error("Pick when to send");
      return;
    }
    setSaving(true);
    try {
      await api.post("/followups", {
        customer_id: deal.customer_id,
        deal_id: deal.id,
        channel,
        scheduled_at: new Date(when).toISOString(),
        subject: isEmail ? subject : "",
        message,
        status: "approved", // deliberately scheduled → queued, ready to send when due
      });
      toast.success(`Follow-up scheduled · ${isEmail ? "Email" : "WhatsApp"}`);
      onScheduled?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not schedule follow-up");
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Schedule Follow-up"
      subtitle={`${deal?.customer_name || customer?.company || ""} · ${m.label} stage`}
      onClose={onClose}
      size="lg"
    >
      <div className="grid gap-4" data-testid="followup-composer">
        <div style={{ fontSize: 12, color: "var(--text-2)" }}>
          Pre-filled from the lead's <strong>{m.label}</strong> stage. Opens Gmail or WhatsApp for you to send when it's due — edit anything below.
        </div>

        {/* Channel */}
        <div>
          <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Channel</label>
          <div className="flex gap-2">
            {[
              ["whatsapp", "WhatsApp", MessageCircle],
              ["email", "Email", Mail],
            ].map(([key, label, Icon]) => (
              <button
                key={key}
                type="button"
                onClick={() => setChannel(key)}
                className={channel === key ? "btn-primary" : "btn-outline"}
                style={{ padding: "7px 14px", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}
                data-testid={`followup-channel-${key}`}
              >
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: recipient ? "var(--text-2)" : "var(--danger)", marginTop: 6, display: "flex", alignItems: "center", gap: 4 }}>
            {recipient ? (
              <>Sends to <strong style={{ color: "var(--text)" }}>{recipient}</strong></>
            ) : (
              <><AlertTriangle size={11} /> No {isEmail ? "email" : "phone"} on this customer</>
            )}
          </div>
        </div>

        {/* When */}
        <div>
          <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>When to send</label>
          <div className="flex gap-2 flex-wrap items-center">
            {PRESETS.map((p) => {
              const active = when === plusDaysLocal(p.days);
              return (
                <button
                  key={p.days}
                  type="button"
                  onClick={() => setWhen(plusDaysLocal(p.days))}
                  className={active ? "btn-primary" : "btn-outline"}
                  style={{ padding: "6px 12px", fontSize: 12 }}
                  data-testid={`followup-when-${p.days}`}
                >
                  {p.label}
                </button>
              );
            })}
            <input
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              className="input-base font-mono-data"
              style={{ width: "auto", padding: "6px 10px", fontSize: 13 }}
              data-testid="followup-when-custom"
            />
          </div>
        </div>

        {isEmail && (
          <div>
            <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="input-base" data-testid="followup-subject" />
          </div>
        )}

        <div>
          <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Message</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={isEmail ? 12 : 9}
            className="input-base"
            style={{ resize: "vertical", fontFamily: "inherit", whiteSpace: "pre-wrap" }}
            data-testid="followup-message"
          />
        </div>

        <div className="flex gap-3 justify-end mt-2">
          <button onClick={onClose} className="btn-outline" disabled={saving} data-testid="followup-cancel">Cancel</button>
          <button onClick={schedule} className="btn-primary" disabled={saving} style={{ display: "flex", alignItems: "center", gap: 6 }} data-testid="followup-schedule">
            <CalendarClock size={14} />
            {saving ? "Scheduling…" : "Schedule Follow-up"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
